/**
 * Drafts (Slack "Drafts & sent", contracts drafts.ts): which copy of a conversation's draft to show (this device's or
 * the server's — the newer one), the queue of saves waiting for the network, and the Drafts list. Shared by every
 * platform so they agree on what wins.
 */
import type { Draft, SaveDraftRequest } from "@bundle/contracts";

/** How long the composer waits after the last keystroke before saving the draft to the server. */
export const DRAFT_SYNC_DELAY_MS = 1500;
/** Longest preview line in the Drafts list. */
export const DRAFT_PREVIEW_MAX = 140;

/** This device's copy: the text and when it was typed (`at` 0 = before drafts had times). */
export type LocalDraft = { text: string; at: number };

/** The key of a conversation's composer: the channel's, or one thread's. */
export function draftScope(channelId: string, threadRootId?: string): string {
  return threadRootId === undefined ? channelId : `${channelId}.${threadRootId}`;
}

/**
 * Which draft the composer shows: the newer of this device's and the server's (a tie keeps this device's). An empty
 * local copy with a time is a deliberate clear (it wins over an older server draft); without a time it is no draft.
 * `push` = the server should get this device's copy (it is newer and differs).
 */
export function pickDraft(local: LocalDraft | undefined, remote: Draft | undefined): { text: string; at: number; push: boolean } {
  const l = local ?? { text: "", at: 0 };
  if (remote === undefined) return { text: l.text, at: l.at, push: l.text.trim() !== "" };
  if (remote.updatedAt > l.at) return { text: remote.text, at: remote.updatedAt, push: false };
  return { text: l.text, at: l.at, push: l.text !== remote.text };
}

/**
 * Saves waiting for the network, one per conversation (the latest replaces an older one; an older one never
 * replaces a newer one).
 */
export function queueSave(pending: Readonly<Record<string, SaveDraftRequest>>, req: SaveDraftRequest): Record<string, SaveDraftRequest> {
  const key = draftScope(req.channelId, req.threadRootId);
  const cur = pending[key];
  if (cur !== undefined && cur.updatedAt > req.updatedAt) return { ...pending };
  return { ...pending, [key]: req };
}

/** Most recent first (ties by id, newest first). */
export function sortDrafts(list: readonly Draft[]): Draft[] {
  return [...list].sort((a, b) => b.updatedAt - a.updatedAt || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
}

/** Puts a saved draft in the list (replacing that conversation's), or takes the conversation's out when absent. */
export function applySaved(list: readonly Draft[], channelId: string, threadRootId: string | undefined, saved: Draft | undefined): Draft[] {
  const rest = list.filter((d) => !(d.channelId === channelId && d.threadRootId === threadRootId));
  return sortDrafts(saved === undefined || saved.text.trim() === "" ? rest : [saved, ...rest]);
}

export function removeDraft(list: readonly Draft[], id: string): Draft[] {
  return list.filter((d) => d.id !== id);
}

/** The list's one-line preview: whitespace collapsed, cut at DRAFT_PREVIEW_MAX characters with an ellipsis. */
export function draftPreview(text: string): string {
  const line = text.replace(/\s+/g, " ").trim();
  const chars = Array.from(line);
  return chars.length <= DRAFT_PREVIEW_MAX ? line : `${chars.slice(0, DRAFT_PREVIEW_MAX - 1).join("").trimEnd()}…`;
}
